import {Component, Input, OnInit} from '@angular/core';
import {ReferenceBookType, RefundResponse} from '@npck/af-frontend-api';
import {RouterService, rxSpinner} from '@npck/libs/commons';
import {combineLatest} from 'rxjs';
import {mapToViewModel} from '../../../../components/files/file-preview/file-preview.component';
import {DictionaryHolderService} from '../../../../domain/dictionary-holder.service';
import {
    Dictionaries,
    FieldBuilderData,
    FieldBuilderService,
    FieldInfo,
    PARTICIPANT_FIELD_NAMES,
    PARTICIPANT_NESTED_FIELD,
} from '../../../../domain/field-builder.service';
import {INCIDENT_ID, REFUND_ID, TRANSACTION_ID} from '../../../../domain/query-params.keys';
import {Path} from '../../../../path';
import {AttachmentViewModel} from '../../../incidents/details/documents-tab/documents-tab.component';

@Component({
    selector: 'af-refund-details-tab',
    standalone: false,
    templateUrl: './refund-details-tab.component.html',
    styleUrl: './refund-details-tab.component.scss',
})
export class RefundDetailsTabComponent implements OnInit {
    @Input() refund!: RefundResponse;

    commonFieldInfo: FieldInfo[];
    transactionFieldInfo: FieldInfo[];
    senderFields: FieldInfo[];
    receiverFields: FieldInfo[];
    attachments: AttachmentViewModel[] = [];
    paymentOrderAttachments: AttachmentViewModel[] = [];

    private commonFieldNames: (keyof RefundResponse)[] = [
        'type',
        'transactionIds',
        'prosecutorOfficeBin',
        'grounds',
        'kuiNumber',
        'kuiNumberDate',
        'resolutionAmount',
        'resolutionCurrency',
        'description',
    ];

    private transactionFieldNames: (keyof RefundResponse)[] | string[] = [
        'transactionCategory',
        'transactionType',
        'created_date',
        'transactionAmount',
        'transactionCurrency',
    ];

    constructor(
        private dictionaryHolderService: DictionaryHolderService,
        private fieldBuilderService: FieldBuilderService,
        private routerService: RouterService
    ) {
    }

    ngOnInit(): void {
        combineLatest(
            [
                this.dictionaryHolderService.getReference(ReferenceBookType.REFUND_TYPE),
                this.dictionaryHolderService.getReference(ReferenceBookType.REFUND_GROUNDS),
                this.dictionaryHolderService.getReference(ReferenceBookType.BIN),
                this.dictionaryHolderService.getReference(ReferenceBookType.CURRENCY),
                this.dictionaryHolderService.getReference(ReferenceBookType.TRANSACTION_CATEGORY),
                this.dictionaryHolderService.getReference(ReferenceBookType.TRANSACTION_TYPE),
                this.dictionaryHolderService.getReference(ReferenceBookType.INCIDENT_ROLE),
                this.dictionaryHolderService.getReference(ReferenceBookType.ATTACHED_DOC),
                this.dictionaryHolderService.getReference(ReferenceBookType.ACCOUNT_STATUS),
            ],
        )
            .pipe(rxSpinner('initial'))
            .subscribe(([types, grounds, bins, currencies, transactionCategories, transactionTypes, roles, docTypes, accountStatuses]) => {
                this.attachments = this.refund.attachments
                    .filter(attachment => !['PROSECUTOR_ORDER', 'PAYMENT_ORDER'].includes(attachment.docType))
                    .map(attachment => mapToViewModel(attachment, docTypes));
                this.paymentOrderAttachments = this.refund.attachments
                    .filter(attachment => attachment.docType === 'PAYMENT_ORDER')
                    .map(attachment => mapToViewModel(attachment, docTypes));
                const dictionaries: Dictionaries = {
                    refundTypes: types,
                    grounds, bins, currencies, transactionCategories, transactionTypes, roles, accountStatuses,
                };
                this.commonFieldInfo = this.fieldBuilderService.buildFields(
                    {
                        data: this.refund,
                        fieldNames: this.commonFieldNames,
                        dictionaries: dictionaries,
                        context: 'refund',
                        fieldClick: {
                            'transactionIds': (id) => this.navigateToTransaction(id),
                        },
                    },
                );
                this.transactionFieldInfo = this.fieldBuilderService.buildFields(
                    {
                        data: this.refund,
                        fieldNames: this.transactionFieldNames,
                        dictionaries: dictionaries,
                    },
                );

                this.senderFields = this.fieldBuilderService.buildFields(<FieldBuilderData>{
                    fieldNames: PARTICIPANT_FIELD_NAMES,
                    nestedFieldNames: PARTICIPANT_NESTED_FIELD,
                    data: this.refund.sender,
                    dictionaries: dictionaries,
                    context: 'participant',
                    type: this.refund.sender.type,
                });
                this.receiverFields = this.fieldBuilderService.buildFields(<FieldBuilderData>{
                    fieldNames: PARTICIPANT_FIELD_NAMES,
                    nestedFieldNames: PARTICIPANT_NESTED_FIELD,
                    data: this.refund.receiver,
                    dictionaries: dictionaries,
                    context: 'participant',
                    type: this.refund.receiver.type,
                });
            });
    }

    private navigateToTransaction(id: string): void {
        this.routerService.navigate(Path.REFUNDS_TRANSACTIONS_DETAILS, {
            pathParams: {
                [REFUND_ID]: this.refund.id,
                [TRANSACTION_ID]: id,
            },
            queryParams: {
                [INCIDENT_ID]: this.refund.incidentId,
            },
        });
    }
}
